import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client.js";

export function SharedContextPanel({ topologyId }: { topologyId: string }) {
  const { data: kvEntries } = useQuery({
    queryKey: ["context", topologyId, "kv"],
    queryFn: () => api.context.list(topologyId),
    refetchInterval: 5000,
  });

  const { data: structuredEntries } = useQuery({
    queryKey: ["context", topologyId, "entries"],
    queryFn: () => api.context.entries(topologyId),
    refetchInterval: 5000,
  });

  const hasContent =
    (kvEntries && kvEntries.length > 0) ||
    (structuredEntries && structuredEntries.length > 0);

  if (!hasContent) return null;

  return (
    <div>
      <div className="px-5 pt-4 pb-2">
        <span className="text-xs text-text-muted font-medium">
          Shared Context
        </span>
      </div>

      {/* KV entries */}
      {kvEntries && kvEntries.length > 0 && (
        <div className="px-5 py-3 space-y-1.5">
          <div className="text-[11px] text-text-muted mb-1">
            Key-Value Store
          </div>
          {kvEntries.map((entry: any) => (
            <div
              key={entry.key}
              className="bg-surface/30 rounded-lg px-3 py-2 text-sm"
            >
              <span className="text-brand-cyan font-mono text-xs">{entry.key}</span>
              <span className="text-text-muted mx-2">=</span>
              <span className="text-text-primary text-xs">
                {typeof entry.value === "string"
                  ? entry.value.slice(0, 50)
                  : JSON.stringify(entry.value).slice(0, 50)}
              </span>
              {entry.meta?.author && (
                <span className="text-text-muted ml-2 text-[11px]">
                  by {entry.meta.author}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Structured entries */}
      {structuredEntries && structuredEntries.length > 0 && (
        <div className="px-5 py-3 space-y-1.5">
          <div className="text-[11px] text-text-muted mb-1">
            Structured Entries
          </div>
          {structuredEntries.map((entry: any) => (
            <StructuredEntryCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_COLORS: Record<string, string> = {
  artifact: "text-yellow-400 bg-yellow-400/10",
  decision: "text-brand-purple bg-brand-purple/10",
  plan: "text-brand-cyan bg-brand-cyan/10",
  observation: "text-brand-green bg-brand-green/10",
};

function StructuredEntryCard({ entry }: { entry: any }) {
  const kindColor = KIND_COLORS[entry.kind] ?? "text-text-secondary bg-surface/30";

  let title = "";
  let subtitle = "";

  switch (entry.kind) {
    case "artifact":
      title = entry.name ?? "Artifact";
      subtitle = `${entry.mimeType} · ${entry.stage}`;
      break;
    case "decision":
      title = entry.decision ?? "Decision";
      subtitle = `Confidence: ${Math.round((entry.confidence ?? 0) * 100)}% · ${entry.status}`;
      break;
    case "plan":
      title = entry.title ?? "Plan";
      subtitle = `${entry.steps?.length ?? 0} steps`;
      break;
    case "observation":
      title = entry.observation?.slice(0, 60) ?? "Observation";
      subtitle = `${entry.category} · Importance: ${entry.importance}`;
      break;
  }

  return (
    <div className="bg-surface/30 rounded-lg px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <span className={`px-1.5 py-0.5 rounded text-[11px] ${kindColor}`}>
          {entry.kind}
        </span>
        <span className="text-text-primary truncate text-xs">{title}</span>
      </div>
      <div className="text-[11px] text-text-muted mt-1 tabular-nums">{subtitle}</div>
    </div>
  );
}
